/**
 * ResumePreview component renders ResumeContent JSON as styled HTML.
 * Used in the preview panel to show real-time resume updates.
 *
 * Renders at full paper width (816px = 8.5") and scales down to fit container.
 * Supports multiple templates with different layouts:
 * - Modern: Single column, clean design
 * - Two-Column (pikachu): Sidebar with contact/skills, main area with experience
 * - Executive: Single column, formal design
 */

import { cn } from '@/lib/utils'
import type { ResumeContent, TemplateId } from '@/types'
import { useEffect, useRef, useState } from 'react'

// Standard letter paper width in pixels (8.5" at 96dpi)
const PAPER_WIDTH = 816

interface ResumePreviewProps {
  content: ResumeContent
  template?: TemplateId
  className?: string
}

// Template-specific styles
const templateStyles = {
  modern: {
    container: 'font-sans',
    header: 'border-b-2 border-indigo-600 pb-4 mb-6',
    sidebar: '', // Not used - single column
    sidebarHeader: '',
    sidebarSection: '',
    sidebarTitle: '',
    sidebarText: '',
    name: 'text-2xl font-bold text-indigo-900',
    contactInfo: 'flex flex-wrap gap-3 text-sm text-gray-600 mt-2',
    section: 'mb-5',
    sectionTitle: 'text-base font-semibold text-indigo-800 border-b border-gray-200 pb-1 mb-3',
    jobTitle: 'font-semibold text-gray-900',
    company: 'text-gray-700',
    dates: 'text-sm text-gray-500',
    bullet: 'text-gray-700 ml-4 list-disc',
    link: 'text-indigo-600 hover:underline',
    skillBg: 'bg-indigo-50 text-indigo-700',
  },
  pikachu: {
    container: 'font-sans',
    header: 'bg-indigo-600 text-white p-5 -mx-8 -mt-8 mb-5', // Full-width indigo header
    sidebar: '', // Not used - single column
    sidebarHeader: '',
    sidebarSection: '',
    sidebarTitle: '',
    sidebarText: '',
    name: 'text-2xl font-bold text-white mb-2',
    contactInfo: 'flex flex-wrap gap-4 text-sm text-indigo-100',
    section: 'mb-4',
    sectionTitle:
      'text-sm font-bold text-indigo-600 uppercase tracking-wide border-b-2 border-indigo-600 pb-1 mb-3',
    jobTitle: 'font-semibold text-gray-900',
    company: 'text-indigo-600',
    dates: 'text-sm text-gray-500',
    bullet: 'text-gray-600 ml-4 list-disc',
    link: 'text-indigo-600 hover:underline',
    skillBg: 'bg-gray-100 border border-gray-200 text-gray-600',
  },
  executive: {
    container: 'font-serif',
    header: 'text-center border-b-2 border-slate-700 pb-4 mb-6',
    sidebar: '', // Not used - single column
    sidebarHeader: '',
    sidebarSection: '',
    sidebarTitle: '',
    sidebarText: '',
    name: 'text-3xl font-bold text-slate-800 tracking-wide',
    contactInfo: 'flex flex-wrap justify-center gap-1 text-sm text-gray-600 mt-3',
    section: 'mb-5',
    sectionTitle:
      'text-sm font-bold text-slate-800 uppercase tracking-widest border-b border-slate-300 pb-1 mb-3',
    jobTitle: 'font-bold text-slate-800',
    company: 'text-slate-600',
    dates: 'text-sm text-gray-500 italic',
    bullet: 'text-gray-700 ml-4 list-disc',
    link: 'text-slate-700 hover:underline',
    skillBg: 'bg-gray-100 text-gray-700',
  },
}

export function ResumePreview({ content, template = 'modern', className }: ResumePreviewProps) {
  const styles = templateStyles[template]
  const containerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  // Calculate scale factor based on container width
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth
        const newScale = Math.min(1, containerWidth / PAPER_WIDTH)
        setScale(newScale)
      }
    }

    updateScale()
    window.addEventListener('resize', updateScale)
    return () => window.removeEventListener('resize', updateScale)
  }, [])

  // All templates now use single-column layout for PDF compatibility
  return (
    <div ref={containerRef} className={cn('overflow-hidden', className)}>
      <div
        style={{
          width: PAPER_WIDTH,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
        className={cn('bg-white p-8 text-sm', styles.container)}
      >
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.name}>{content.contact.name}</h1>
          <div className={styles.contactInfo}>
            {template === 'executive' ? (
              // Executive: pipe-separated contact info
              <>
                {[content.contact.email, content.contact.phone, content.contact.location]
                  .filter(Boolean)
                  .map((item, idx, arr) => (
                    <span key={String(item)}>
                      {item}
                      {idx < arr.length - 1 && <span className="mx-2 text-gray-400">|</span>}
                    </span>
                  ))}
              </>
            ) : (
              // Modern: spaced contact info
              <>
                {content.contact.email && <span>{content.contact.email}</span>}
                {content.contact.phone && <span>{content.contact.phone}</span>}
                {content.contact.location && <span>{content.contact.location}</span>}
                {content.contact.website && (
                  <a
                    href={content.contact.website}
                    className={styles.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Website
                  </a>
                )}
              </>
            )}
          </div>
        </div>

        {/* Summary */}
        {content.summary && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {template === 'executive' ? 'Professional Summary' : 'Summary'}
            </h2>
            <p
              className={cn(
                'text-gray-700 leading-relaxed',
                template === 'executive' && 'text-justify',
              )}
            >
              {content.summary}
            </p>
          </section>
        )}

        {/* Experience */}
        {content.experience && content.experience.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {template === 'executive' ? 'Professional Experience' : 'Experience'}
            </h2>
            <div className={cn('space-y-4', template === 'pikachu' && 'divide-y divide-gray-200')}>
              {content.experience.map((exp) => (
                <div
                  key={`${exp.company}-${exp.title}-${exp.start_date}`}
                  className={template === 'pikachu' ? 'pt-3 first:pt-0' : ''}
                >
                  <div className="flex justify-between items-start">
                    {template === 'pikachu' ? (
                      // Pikachu: stacked job title and company
                      <div>
                        <div className={styles.jobTitle}>{exp.title}</div>
                        <div className={styles.company}>{exp.company}</div>
                      </div>
                    ) : template === 'executive' ? (
                      // Executive: comma-separated on one line
                      <div>
                        <span className={styles.jobTitle}>{exp.title}</span>
                        <span className="text-gray-500">, {exp.company}</span>
                      </div>
                    ) : (
                      // Modern: "at" separator with location
                      <div>
                        <span className={styles.jobTitle}>{exp.title}</span>
                        <span className="text-gray-500"> at </span>
                        <span className={styles.company}>{exp.company}</span>
                        {exp.location && (
                          <span className="text-sm text-gray-500 ml-1">({exp.location})</span>
                        )}
                      </div>
                    )}
                    {template === 'pikachu' ? (
                      // Pikachu: stacked dates and location
                      <div className="text-right">
                        <div className={styles.dates}>
                          {exp.start_date} - {exp.end_date || 'Present'}
                        </div>
                        {exp.location && (
                          <div className="text-xs text-gray-500">{exp.location}</div>
                        )}
                      </div>
                    ) : (
                      // Modern/Executive: single line with location
                      <span className={styles.dates}>
                        {exp.start_date} - {exp.end_date || 'Present'}
                        {exp.location && template === 'executive' && ` | ${exp.location}`}
                      </span>
                    )}
                  </div>
                  {exp.achievements && exp.achievements.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {exp.achievements.map((achievement, idx) => (
                        <li key={`${exp.company}-ach-${idx}`} className={styles.bullet}>
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Education */}
        {content.education && content.education.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Education</h2>
            <div className="space-y-3">
              {content.education.map((edu) => (
                <div
                  key={`${edu.institution}-${edu.degree}`}
                  className="flex justify-between items-start"
                >
                  <div>
                    <span className={styles.jobTitle}>
                      {edu.degree}
                      {edu.field_of_study && ` in ${edu.field_of_study}`}
                    </span>
                    <div className={styles.company}>{edu.institution}</div>
                    {edu.gpa && <span className="text-sm text-gray-500">GPA: {edu.gpa}</span>}
                    {edu.honors && edu.honors.length > 0 && (
                      <div className="text-sm text-gray-600">{edu.honors.join(', ')}</div>
                    )}
                  </div>
                  {edu.graduation_date && (
                    <span className={styles.dates}>{edu.graduation_date}</span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Skills */}
        {content.skills && content.skills.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Skills</h2>
            <div className="flex flex-wrap gap-2">
              {content.skills.map((skill) => (
                <span key={skill} className={cn('px-2 py-1 rounded text-xs', styles.skillBg)}>
                  {skill}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Certifications */}
        {content.certifications && content.certifications.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Certifications</h2>
            <ul className="space-y-1">
              {content.certifications.map((cert) => (
                <li key={cert} className="text-gray-700">
                  {cert}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Projects */}
        {content.projects && content.projects.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Projects</h2>
            <div className="space-y-3">
              {content.projects.map((project) => (
                <div key={project.name}>
                  <div className="flex items-center gap-2">
                    <span className={styles.jobTitle}>{project.name}</span>
                    {project.url && (
                      <a
                        href={project.url}
                        className={cn('text-sm', styles.link)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Link
                      </a>
                    )}
                  </div>
                  {project.description && (
                    <p className="text-gray-700 text-sm mt-1">{project.description}</p>
                  )}
                  {project.technologies && project.technologies.length > 0 && (
                    <div className="text-sm text-gray-500 mt-1">
                      Technologies: {project.technologies.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Languages */}
        {content.languages && content.languages.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Languages</h2>
            <p className="text-gray-700">{content.languages.join(', ')}</p>
          </section>
        )}
      </div>
    </div>
  )
}
